<script lang="ts" setup>
/**
 * diagram     https://gitlab.com/qliqdev/qliqweb-vue/-/blob/dev/docs/marmaid/pc/chat-messages.md?ref_type=heads
 */

import Axios, { AxiosError } from 'axios'
import { ElMessage } from 'element-plus'
import { storeToRefs } from 'pinia'
import {
  computed,
  defineAsyncComponent,
  onMounted,
  ref,
  toRef,
  watch,
} from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import ChatMessageStatus from '@shared/components/ChatMessageStatus.vue'
import TypingIndicator from '@shared/components/TypingIndicator.vue'
import ChatMessageActions from '@shared/components/chat/ChatMessageActions.vue'
import ChatMessageDetails from '@shared/components/chat/ChatMessageDetails.vue'
import MarkdownMessageText from '@shared/components/chat/MarkdownMessageText.vue'
import MessageDetailsWrapper from '@shared/components/chat/MessageDetailsWrapper.vue'
import AiMessage from '@shared/components/chat/gpt/TheMessage.vue'
import { useMessageText } from '@shared/composables/chat/use-message-text'
import { useModal } from '@shared/composables/use-modal'
import { useSystemMessage } from '@shared/composables/use-system-message'
import { AttachmentSender } from '@shared/models/enums/attachments'
import { HttpStatus } from '@shared/models/enums/http'
import {
  BotPayload,
  MessageContent,
} from '@shared/models/enums/one-of-kind-enums'
import { AiTypingStatus } from '@shared/models/enums/typing-indicator'
import { AcResults } from '@shared/models/interfaces/conversations'
import { IMessage } from '@shared/models/proto/base/messages'
import { Nullable } from '@shared/models/types/primary'
import { useTypingIndicatorsStore } from '@shared/store/typing-indicators'
import { useVideoCallConfigsStore } from '@shared/store/video-call-configs'
import { useFetchData } from '@shared/utils/composables/fetch-data'
import { useMessageDate } from '@shared/utils/composables/use-message-date'
import { getFirstArrayItem } from '@shared/utils/helpers/array/get-first-array-item'
import { attachmentFactory } from '@shared/utils/helpers/attachments/attachment-factory'
import {
  getAttachmentType,
  getAttachmentTypeByMessageType,
} from '@shared/utils/helpers/attachments/get-attachment-type'
import { getStatusIcon } from '@shared/utils/helpers/chat/message-status'
import { getAvatarData } from '@shared/utils/helpers/chat/participant-avatar'
import { getSenderName } from '@shared/utils/helpers/chat/sender-name'
import { downloadUrl } from '@shared/utils/helpers/download-url'
import { isYoutubeOrVimeoLink } from '@shared/utils/helpers/embed-urls/embedded-link'
import { getErrorMessages } from '@shared/utils/helpers/errors/error-handling'
import { displayErrorMessages } from '@shared/utils/helpers/errors/error-message'
import { isMobileDevice } from '@shared/utils/helpers/is-mobile-device'
import {
  getGptConfig,
  getGptUuid,
  isGptBotPayload,
} from '@shared/utils/helpers/messages/bot-payload'
import { isPending } from '@shared/utils/helpers/request-status'
import { isHttpsOrHttpPresented } from '@shared/utils/helpers/url-formatter'

import { Conversation } from '@protos/apps/pc/conversation_pb'
import { Show as QuickFormShowDTO } from '@protos/apps/pc/request/quick_form/show_pb'
import { Attachment as Attachment_PB } from '@protos/chat/attachment_pb'
import { EmbeddedAttachment } from '@protos/chat/embedded_attachment_pb'
import { Message } from '@protos/chat/message_pb'
import { MessageType } from '@protos/chat/message_type_pb'
import { QuickForm } from '@protos/chat/quick_form_pb'
import { ConversationStatus } from '@protos/conversation_enums_pb'
import {
  QuickSignDocument_Status,
  QuickSignDocument_QuickSignSigner_Status as QuickSignStatus,
} from '@protos/quick_sign_document_pb'
import { ApiErrorResponse } from '@protos/response/api_error_response_pb'
import { UserType } from '@protos/user_type_pb'

import { PlainMessage } from '@bufbuild/protobuf'
import { useAiRefreshedMessageScroll } from '@pc/composables/use-ai-refreshed-message-scroll'
import { useChatMessages } from '@pc/composables/use-chat-messages'
import { useQuickFormOpening } from '@pc/composables/use-quick-form-opening'
import { isFormSubmission } from '@pc/helpers/is-form-submission'
import { useMessageAttachmentsStore } from '@pc/store/message-attachments'
import { useMessagesStore } from '@pc/store/messages'
import { useQuickFormsStore } from '@pc/store/quick-forms'
import { useQuickSignStore } from '@pc/store/quick-sign'
import { useSessionStore } from '@pc/store/session'
import { useSocketStore } from '@pc/store/socket'
import { JsonValue } from '@protobuf-ts/runtime'

const UserAttachment = defineAsyncComponent(
  () => import('@pc/components/attachments/UserAttachment.vue')
)
const MessageDate = defineAsyncComponent(
  () => import('@shared/components/MessageDate.vue')
)
const SystemMessageBubble = defineAsyncComponent(
  () => import('@shared/components/SystemMessageBubble.vue')
)
const ChatUserAvatar = defineAsyncComponent(
  () => import('@shared/components/chat/ChatUserAvatar.vue')
)
const BotAttachment = defineAsyncComponent(
  () => import('@pc/components/attachments/BotAttachment.vue')
)
const BaseObserver = defineAsyncComponent(
  () => import('@shared/components/BaseObserver.vue')
)
const FileModal = defineAsyncComponent(
  () => import('@shared/components/modals/FileModal.vue')
)
const EmbeddedLink = defineAsyncComponent(
  () => import('@shared/components/attachments/EmbeddedLink.vue')
)

const props = withDefaults(
  defineProps<{
    message: IMessage
    isMe: boolean
    isLastMessage?: boolean
    isAnimatedMessage?: boolean
    conversation?: Nullable<PlainMessage<Conversation>>
    items: Array<IMessage>
    index: number
    isTyping?: boolean
    isNewSender?: boolean
    fetchQuickForm: (
      dto: PlainMessage<QuickFormShowDTO>
    ) => Promise<PlainMessage<QuickForm>>
  }>(),
  { isLastMessage: false, isNewSender: false }
)
const emit = defineEmits<{
  (event: 'send-ac-results', data: AcResults): void
  (event: 'mark-read', message: IMessage): void
  (event: 'recall', message: IMessage): void
  (event: 'scroll-to-bottom'): void
}>()

const router = useRouter()
const { isVisible: isVisibleFileModal, setVisible } = useModal()
const sessionStore = useSessionStore()
const messageAttachmentsStore = useMessageAttachmentsStore()
const { t } = useI18n()
const { isSystemMessage } = useSystemMessage(props.message.messageType)
const { isDifferentDay, getMessageDate } = useMessageDate()
const videoCallConfigsStore = useVideoCallConfigsStore()
const { isVideoChatMode } = storeToRefs(videoCallConfigsStore)
const { fetchData } = useFetchData()
const { fetchData: fetchAttachment, isLoading: isFetchingAttachment } =
  useFetchData()

const { refreshedQuestionMessageId, needToAddPadding } =
  useAiRefreshedMessageScroll()
const socketStore = useSocketStore()

const { fetchData: fetchFlattenDocumentUrl, isLoading } = useFetchData()
const { fetchData: fetchUpdateQuickSign } = useFetchData()
const quickFormsStore = useQuickFormsStore()
const {
  sendUserMessage,
  lastMessage,
  isLastMessageAssigningToAI,
  isAiAssigned,
} = useChatMessages()
const {
  messageText,
  isVisitor,
  isMessageToAi,
  isSanitized,
  messageTime,
  isShowMessage,
  isBotMessage,
  isRecalled,
} = useMessageText(toRef(props, 'message'), toRef(props, 'isMe'))

const quickSignStore = useQuickSignStore()
const messagesStore = useMessagesStore()
const typingStore = useTypingIndicatorsStore()
const { isLoadingResources } = storeToRefs(messagesStore)
const { aiTypingStatus } = storeToRefs(typingStore)

const { onQuickFormOpenHandler } = useQuickFormOpening()

const selectedAttachment = ref()
const isAutoOpened = ref(false)

const shouldHideSystemMessage = computed(
  () => sessionStore.chatProps?.chatWidget?.hideAssignmentBanners
)

const isAutoPlayMessage = computed(
  () => botPayload.value?.autoplay && props.isLastMessage
)

const isObserverShown = computed(() => {
  const msg = props.message as Message

  return !msg.actualStatus?.readTime
})

const content = computed(() => props.message.payload?.content)
const botPayload = computed(() =>
  content.value?.case === MessageContent.botPayload
    ? content.value?.value
    : null
)

const botPayloadCase = computed(() => botPayload?.value?.option.case)

const showMessagePayload = computed(() => {
  const oneofKind = botPayloadCase.value
  if (
    !oneofKind ||
    (oneofKind === BotPayload.carousel && props.isLastMessage)
  ) {
    return false
  }

  return true
})

const selectedOptionValue = computed(() => {
  if (
    botPayload?.value?.option.case === BotPayload.carousel &&
    !props.isLastMessage
  ) {
    // text of the next message
    return props.items[props.index + 1]?.text
  }
  return null
})

const form = computed(() =>
  botPayload.value?.option.case === BotPayload.form
    ? botPayload.value.option.value
    : null
)

const formUuid = computed(() => form.value?.uuid ?? '')

const attachmentInitial = computed<Nullable<EmbeddedAttachment>>(() => {
  const messageAttachment = getFirstArrayItem(props.message.attachments)
  if (messageAttachment) {
    return messageAttachment as EmbeddedAttachment
  }
  return null
})
const attachmentLoaded = computed<Nullable<Attachment_PB>>(
  () =>
    messageAttachmentsStore.getAttachment(
      props.message.id
    ) as Nullable<Attachment_PB>
)

const isSigned = computed(() => {
  return (
    attachmentLoaded.value?.quickSignDocument?.status ===
      QuickSignDocument_Status.SUBMITTED ||
    attachmentLoaded.value?.quickSignDocument?.status ===
      QuickSignDocument_Status.COMPLETED
  )
})

const isAttachmentLoading = computed(() =>
  messageAttachmentsStore.getIsLoading(props.message.id)
)

const isShowTypingIndicator = computed(() => {
  const isLastMessageAndIsTyping = props.isTyping && props.isLastMessage
  const isLastMessageRepliedAndHasStatus =
    aiTypingStatus.value !== AiTypingStatus.UNKNOWN &&
    lastMessage.value?.replyToChatMessageId === props.message.id

  return (
    isLastMessageRepliedAndHasStatus ||
    isLastMessageAndIsTyping ||
    isRefreshingAiMessage.value
  )
})
const recallActionShow = computed(() => {
  const isPreviousMessageToBot =
    props.index >= 1 &&
    props.items[props?.index - 1]?.sender?.type === UserType.BOT

  return (
    !isGptBotPayload(props.message) &&
    !isBotMessage.value &&
    !isRecalled.value &&
    !isMessageToAi.value &&
    !isPreviousMessageToBot &&
    props.isMe
  )
})

const quickFormByUuid = computed(() =>
  formUuid.value
    ? quickFormsStore.getQuickFormByUuid(formUuid.value)
    : undefined
)

const isAiChatEnded = computed(() => {
  return (
    !isAiAssigned.value ||
    (isAiAssigned.value && isLastMessageAssigningToAI.value) ||
    props.conversation?.status === ConversationStatus.CLOSE
  )
})

const isEmbed = computed(
  () =>
    props.message?.messageType === MessageType.EMBED ||
    isYoutubeOrVimeoLink(props.message.text)
)

const messageType = computed(() =>
  getAttachmentTypeByMessageType(props.message.messageType)
)

const hideAttachment = computed(() => !props.isMe && isRecalled.value)

const isRefreshingAiMessage = computed<boolean>(
  () =>
    refreshedQuestionMessageId.value === props.message.id &&
    needToAddPadding.value
)

const isQuickSign = computed(() => {
  if (!attachmentInitial.value) {
    return
  }

  const { contentType } = attachmentInitial.value

  return getAttachmentType(contentType).quickSign.isShow
})

const shouldHandleUpdate = ref(true)
const handleVNodeUpdated = () => {
  if (shouldHandleUpdate.value) {
    return
  } // Prevent execution
  emit('scroll-to-bottom')
  shouldHandleUpdate.value = true
}

// Watch for form UUID changes and fetch data if necessary
watch(
  formUuid,
  async (quickFormUuid) => {
    if (
      quickFormUuid &&
      !isPending(quickFormByUuid.value) &&
      !quickFormByUuid.value?.data?.id
    ) {
      await fetchData({
        async onFetchDataCallback() {
          await props.fetchQuickForm({
            id: {
              case: 'quickFormUuid',
              value: quickFormUuid,
            },
            conversationId: props.conversation?.id ?? 0,
          })
        },
        onErrorCatchCallback: () => {
          ElMessage.error(t('pt.quickFormPage.failedToLoadForm'))
        },
      })
    }
  },
  {
    immediate: true,
  }
)

const isShowTitle = computed(() => {
  const { messageType } = selectedAttachment.value || {}
  const doNotShowTitle =
    selectedAttachment.value &&
    (getAttachmentTypeByMessageType(messageType)?.isImage ||
      getAttachmentTypeByMessageType(messageType)?.isPdf)
  return !doNotShowTitle
})

const onQuickFormOpen = () => {
  const loadedAttachmentId = attachmentLoaded?.value?.id
  const messageId = props.message.id
  if (!loadedAttachmentId && messageId) {
    fetchAttachment({
      onFetchDataCallback: async () => {
        await messageAttachmentsStore.fetchAttachment(messageId)
      },
    })
  }

  const submissionFormUuid =
    attachmentInitial.value?.payload?.case === 'formSubmission'
      ? attachmentInitial.value?.payload?.value?.formUuid
      : undefined

  const attachmentFormUuid =
    attachmentInitial.value?.payload?.case === 'form'
      ? attachmentInitial.value?.payload?.value?.uuid
      : undefined

  onQuickFormOpenHandler({
    status: props.conversation?.status,
    formUuid: attachmentFormUuid || submissionFormUuid,
    messageId: props.message.id,
  })
}

const onUpdateQuickSign = async (payload: { annotation: string }) => {
  await fetchUpdateQuickSign({
    onFetchDataCallback: async () => {
      setVisible(false)
      const quickSignDocument = {
        ...(attachmentLoaded.value?.quickSignDocument || {}),
        latestSigner: {
          ...(attachmentLoaded.value?.quickSignDocument?.latestSigner || {}),
          annotation: payload.annotation,
          status: QuickSignStatus.SIGNED,
        },
      }

      if (quickSignDocument?.id) {
        const payload = {
          annotation: quickSignDocument?.latestSigner?.annotation || '',
          quickSignDocumentId: quickSignDocument.id,
          conversationId: props.conversation?.id ?? 0,
        }
        const messageId = props.message.id
        // start upload attachment
        socketStore.pushFileUploadStarted(payload.conversationId, messageId)

        const updatedQuickSign = await quickSignStore.updateQuickSignDocument(
          payload,
          messageId
        )
        const newAttachment = new Attachment_PB({
          contentType: attachmentInitial.value?.contentType,
          quickSignDocument: {
            ...(updatedQuickSign ||
              attachmentLoaded.value?.quickSignDocument ||
              {}),
          },
        })

        const messageParams = {
          message: {
            messageType: MessageType.QUICK_SIGN,
            text: '',
            attachments: [newAttachment],
          },
          files: [],
        }

        sendUserMessage(messageParams) //iside methos there is  finish upload attachment event
      }
    },
    onErrorCatchCallback: (error) => {
      const { response } = error as AxiosError
      if (response?.status === HttpStatus.UNPROCESSABLE_ENTITY) {
        ElMessage.error(t('pt.quickSign.submitError'))
      }
    },
  })
}

const onMarkRead = () => {
  if (!props.isMe) {
    emit('mark-read', props.message as Message)
  }
}

const recallMessage = () => {
  emit('recall', props.message as Message)
}

// Handle attachment selection
const selectAttachment = (sender: AttachmentSender) => {
  fetchAttachment({
    onFetchDataCallback: async () => {
      let attachmentsUser
      if (sender === AttachmentSender.User) {
        attachmentsUser = await messageAttachmentsStore.fetchAttachment(
          props.message.id
        )
      }

      selectedAttachment.value = attachmentFactory(
        sender,
        botPayload.value,
        attachmentsUser ?? props.message.attachments,
        props.message.messageType
      )

      if (selectedAttachment.value.quickSignDocument?.id && isMobileDevice()) {
        await preFetchQuickSignDocumentUrl()
      }

      setVisible(true)
    },
  })
}

const onCloseFileModal = () => {
  selectedAttachment.value = null
  setVisible(false)
}

const preFetchQuickSignDocumentUrl = async () => {
  // fix iphone download issue
  if (
    selectedAttachment.value.quickSignDocument?.id &&
    !isHttpsOrHttpPresented(
      selectedAttachment.value.quickSignDocument?.flattenedDocumentUrl
    )
  ) {
    const flattenedDocumentUrl = await quickSignStore.fetchFlattenDocument(
      selectedAttachment.value.quickSignDocument?.id,
      props.message.id
    )
    if (flattenedDocumentUrl) {
      selectedAttachment.value = {
        ...selectedAttachment.value,
        quickSignDocument: {
          ...selectedAttachment.value.quickSignDocument,
          flattenedDocumentUrl,
        },
      }
    }
  }
}

const onDownloadUrl = async () => {
  if (
    selectedAttachment.value &&
    selectedAttachment.value.quickSignDocument?.id
  ) {
    await fetchFlattenDocumentUrl({
      onFetchDataCallback: async () => {
        const flattenedDocumentUrl = await quickSignStore.fetchFlattenDocument(
          selectedAttachment.value.quickSignDocument?.id,
          props.message.id
        )

        flattenedDocumentUrl && downloadUrl(flattenedDocumentUrl)
      },
      onErrorCatchCallback: (error) => {
        // eslint-disable-next-line no-console
        console.error('fetchFlattenDocument: ', error)
        if (Axios.isAxiosError(error)) {
          const { response } = error
          const { errors } = ApiErrorResponse.fromJson(
            response?.data as JsonValue
          )
          const messages = getErrorMessages(errors)
          displayErrorMessages(messages)
        }
      },
    })

    return
  } else {
    selectedAttachment.value.url && downloadUrl(selectedAttachment.value.url)
  }
}
// TODO remove it in future
// watch(isAttachmentLoading, () => {
//   // emit('scroll-to-bottom')
// })

/** @description
 * called after onMounted, for clear autoOpen query after redirect from form page
 * */
router.isReady().then(() => {
  if (
    isAutoPlayMessage.value &&
    messageType.value.isQuickForm &&
    !isAutoOpened.value
  ) {
    const query = router.currentRoute.value.query ?? {}
    const autoOpen = Boolean(query.autoOpen)

    if (autoOpen) {
      /** @description
       * need to remove autoOpen query to allow redirect to form page after reload page
       * when message has autoplay prop and is last message  */
      router.replace({
        query: {
          ...query,
          autoOpen: undefined,
        },
      })
    }
  }
})

const autoOpenQuickForm = () => {
  onQuickFormOpenHandler({
    messageId: props.message.id,
    formUuid: formUuid.value,
    autoOpen: true,
  })
  isAutoOpened.value = true
}

const autoOpenMedia = () => {
  const sender = isQuickSign.value
    ? AttachmentSender.User
    : AttachmentSender.Bot

  selectAttachment(sender)
  isAutoOpened.value = true
}

/** @description
 * called first, if url does not have autoOpen query, open media page (quickForm, fileModal)
 * */
onMounted(() => {
  const isQuickForm = messageType.value.isQuickForm
  const payload = props.message.attachments[0]?.payload
  const isFormSubmissionMessage = isFormSubmission(payload)
  const replyMessageId = props.message?.replyToChatMessageId

  handleAutoPlay()

  function handleAutoPlay() {
    if (!isAutoPlayMessage.value) {
      return
    }

    const autoOpen = Boolean(router.currentRoute.value.query.autoOpen)

    if (isQuickForm && !autoOpen) {
      autoOpenQuickForm()
    } else if (messageType.value.isVideo || isQuickSign.value) {
      autoOpenMedia()
    }
  }

  if (isQuickForm && replyMessageId && isFormSubmissionMessage) {
    quickFormsStore.setSubmittedMessageForm(replyMessageId)
  }
})
</script>
<template>
  <MessageDate
    v-if="isDifferentDay(index, items)"
    :date="getMessageDate(index, items)"
  />
  <!--
    Do not combine these conditions.
    If isSystemMessage, we should show the message or show nothing if system messages are turned off
    If you combine the conditions we might show system message as regular message, which is bad
   -->
  <template v-if="isSystemMessage">
    <SystemMessageBubble
      v-if="!shouldHideSystemMessage"
      :text="messageText"
      :time="messageTime.long"
      :participants="conversation?.participants"
      @vnode-mounted="emit('scroll-to-bottom')"
    />
  </template>

  <AiMessage
    v-else-if="isGptBotPayload(message)"
    :key="messageText"
    :config="getGptConfig(message)"
    is-patient
    :is-animated="isAnimatedMessage"
    :time="messageTime.long"
    :message="messageText"
    :message-rating="message?.messageRating"
    :is-loading-resources="isLoadingResources(message.uuid)"
    :is-ai-chat-ended="isAiChatEnded"
    :conversation-id="conversation?.id ?? 0"
    :message-id="message?.id ?? 0"
    :gpt-uuid="getGptUuid(message)"
    :status="getStatusIcon(message)"
    data-ai-message
    @scroll="emit('scroll-to-bottom')"
  />

  <!-- Regular message -->
  <MessageDetailsWrapper
    v-else-if="
      isShowMessage({
        isAttachmentInitial: !!attachmentInitial,
        isAttachmentLoaded: !!attachmentLoaded,
      })
    "
    :id="String(message.id)"
    :key="message.id"
    data-chat-message
    :class="{
      'message-to-ai': isMessageToAi,
      'tw-pb-20': isRefreshingAiMessage,
    }"
  >
    <ChatUserAvatar
      v-if="!isMessageToAi"
      class="tw-mr-4"
      data-avatar
      :avatar="
        getAvatarData({
          participants: conversation?.participants,
          sender: message.sender,
          isNonQliq: false,
        })
      "
      :class="{
        'tw-invisible tw-h-0': !isNewSender,
      }"
      :color="isVisitor ? 'secondary' : 'primary'"
    />

    <div v-if="!isNewSender" class="tw-absolute app-details">
      <AppText
        size="xs"
        color="primaryBlack"
        whitespace="nowrap"
        class="tw-mr-2"
      >
        {{ messageTime.short }}
      </AppText>

      <ChatMessageStatus :shape="getStatusIcon(message)" />
    </div>

    <template #message>
      <div class="message-card tw-w-full">
        <ChatMessageActions v-if="recallActionShow" @recall="recallMessage" />

        <el-row v-if="isEmbed" class="tw-w-full">
          <EmbeddedLink :src="messageText" />
        </el-row>
        <el-row v-else>
          <el-col :span="24">
            <ChatMessageDetails
              v-if="isNewSender && !isMessageToAi"
              data-details
              :sender="
                getSenderName({ sender: message.sender, isMyMessage: isMe })
              "
              :time="messageTime.long"
              :status="getStatusIcon(message)"
              data-sender-details
            />

            <el-row align="middle">
              <el-col class="tw-flex-initial">
                <MarkdownMessageText
                  v-if="isSanitized"
                  :message="messageText"
                />

                <AppText
                  v-else
                  color="primaryBlack"
                  :class="[
                    'app-message',
                    {
                      'tw-line-through': props.isMe && isRecalled,
                    },
                  ]"
                >
                  {{ messageText }}
                </AppText>
              </el-col>

              <template v-if="isMessageToAi">
                <el-col :span="4">
                  <ChatMessageDetails
                    :time="messageTime.long"
                    data-status-details
                    :status="getStatusIcon(message)"
                    class="tw-ml-1.5"
                  />
                </el-col>
                <el-col :span="24">
                  <TypingIndicator
                    v-if="isShowTypingIndicator"
                    data-typing-indicator
                    :text="t(`typingStatuses.${aiTypingStatus}`)"
                  />
                </el-col>
              </template>
            </el-row>
          </el-col>
          <el-skeleton
            animated
            :loading="isAttachmentLoading && !attachmentInitial"
            class="tw-w-full"
          >
            <template #template>
              <el-skeleton-item
                variant="image"
                class="tw-w-[85px] tw-h-[85px]"
              />
            </template>
            <el-col v-if="!hideAttachment" :span="24">
              <BotAttachment
                v-if="showMessagePayload && message.messageType"
                :payload="botPayload"
                :selected-option-value="selectedOptionValue"
                :message-type="message.messageType"
                :conversation-id="props.conversation?.id ?? 0"
                :message-id="message.id"
                :is-last-message="isLastMessage"
                @select-attachment="selectAttachment"
                @loaded="emit('scroll-to-bottom')"
                @vnode-mounted="emit('scroll-to-bottom')"
                @vnode-updated="handleVNodeUpdated"
              />
              <div
                v-if="!messageText && props.isMe && isRecalled"
                class="tw-italic"
              >
                {{ t('chat.messages.recalled') }}
              </div>
              <UserAttachment
                v-if="!botPayloadCase && attachmentInitial"
                :class="{ 'tw-opacity-50': isRecalled }"
                :data="attachmentInitial"
                :message-id="message.id"
                :is-loading="isFetchingAttachment && !isVideoChatMode"
                @loaded="emit('scroll-to-bottom')"
                @vnode-mounted="emit('scroll-to-bottom')"
                @vnode-updated="handleVNodeUpdated"
                @form-selected="onQuickFormOpen"
                @select-attachment="selectAttachment"
              />
            </el-col>
          </el-skeleton>
        </el-row>
      </div>
    </template>
  </MessageDetailsWrapper>

  <FileModal
    v-if="selectedAttachment"
    v-model="isVisibleFileModal"
    :data="selectedAttachment"
    data-file-modal
    :pdf-only-view="isSigned"
    :is-auto-opened="isAutoOpened"
    :read-only-view="isSigned"
    :is-downloading="isLoading"
    :is-show-title="isShowTitle"
    @update-quick-sign="onUpdateQuickSign"
    @on-close="onCloseFileModal"
    @download-url="onDownloadUrl"
  />

  <BaseObserver v-if="isObserverShown" @intersected="onMarkRead" />
</template>

<style lang="scss" scoped>
.message-card {
  @apply tw-relative;

  &:hover {
    :deep(.message-actions) {
      @apply tw-block;
    }
  }
}

.iframe-embed {
  width: 70%;
  height: 600px;
}

:deep(a) {
  text-decoration: underline;
}
</style>

<style lang="scss">
.app-chat-message {
  .app-details {
    @apply tw-hidden;
  }

  .app-status {
    @apply tw-invisible;
  }

  &:hover {
    @apply tw-bg-gray-100;

    .app-details {
      @apply tw-block;
    }

    .app-status {
      @apply tw-visible;
    }
  }
}
</style>
